/**
 * CreateBilagaDialog — the inline "Skapa bilaga" surface opened from an
 * `attachment_ref` picker (ADR-0028 addendum 2026-05-27b, "soft slot").
 *
 * The field defines a default bilaga name (editable here) and may
 * recommend a bilaga template. When a recommended template is available
 * for this parent, the dialog leads with it ("Skapa från mall") and keeps
 * PDF upload as the manual fallback. The created bilaga is referenced by
 * id (Pattern B) — never slot-bound — so its "Bilaga N" stays dynamic.
 *
 * On success the dialog reports the new attachment id back to the caller,
 * which selects it into the field; the editor refetches attachments first
 * so the id resolves immediately.
 */

import { useMemo, useState, type ReactElement } from 'react';
import { Upload, FilePlus, X } from 'lucide-react';
import {
  TEMPLATE_REGISTRY,
  type TemplateModule,
} from '@hyranai/regta-templates';
import {
  BILAGA_ACCEPTED_MIME,
  createBilagaFromTemplate,
  uploadBilaga,
} from '../lib/bilaga-create.js';

type Recommendation = { key: string; version?: string };
type ResolvedRecommendation = {
  key: string;
  version: string;
  module: TemplateModule;
};

function resolveOne(
  rec: Recommendation,
  parentTemplateKey: string,
): ResolvedRecommendation | null {
  const registry = TEMPLATE_REGISTRY as Record<
    string,
    Record<string, TemplateModule>
  >;
  const versions = registry[rec.key];
  if (!versions) return null;
  const entries = Object.entries(versions) as Array<[string, TemplateModule]>;
  const pick = rec.version
    ? entries.find(([v]) => v === rec.version)
    : entries.find(([, m]) => m.metadata.status === 'active') ?? entries[0];
  if (!pick) return null;
  const [version, module] = pick;
  if (module.metadata.status !== 'active') return null;
  if (!(module.metadata.attachableTo ?? []).includes(parentTemplateKey)) {
    return null;
  }
  return { key: rec.key, version, module };
}

function resolveRecommended(
  rec: Recommendation | ReadonlyArray<Recommendation> | undefined,
  parentTemplateKey: string,
): ReadonlyArray<ResolvedRecommendation> {
  if (!rec) return [];
  const list = Array.isArray(rec) ? rec : [rec as Recommendation];
  return list
    .map((r) => resolveOne(r, parentTemplateKey))
    .filter((x): x is ResolvedRecommendation => x !== null);
}

export function CreateBilagaDialog({
  contractId,
  parentTemplateKey,
  defaultName,
  recommendedTemplate,
  onCreated,
  onClose,
}: {
  contractId: string;
  parentTemplateKey: string;
  defaultName?: string;
  recommendedTemplate?: Recommendation | ReadonlyArray<Recommendation>;
  onCreated: (attachmentId: string) => Promise<void> | void;
  onClose: () => void;
}): ReactElement {
  const recommendations = useMemo(
    () => resolveRecommended(recommendedTemplate, parentTemplateKey),
    [recommendedTemplate, parentTemplateKey],
  );
  const hasRecommendations = recommendations.length > 0;
  const [name, setName] = useState(defaultName ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<{ id: string }>) => {
    setBusy(true);
    setErr(null);
    try {
      const created = await fn();
      await onCreated(created.id);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  // For "Skapa från mall" the bilaga takes the template's own name —
  // the field's attachmentDefaultName (and any text the user typed
  // before picking a template) is meant for the PDF-upload fallback,
  // where there's no other source for a sensible name.
  const fromTemplate = (rec: ResolvedRecommendation) => {
    const templateName =
      rec.module.metadata.shortTitle ?? rec.module.metadata.title;
    return void run(() =>
      createBilagaFromTemplate(contractId, rec.key, rec.version, {
        displayName: templateName,
      }),
    );
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void run(() => uploadBilaga(contractId, file, { displayName: name.trim() || undefined }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-stone-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-stone-800">Skapa bilaga</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            title="Stäng"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-stone-700">Namn på bilagan</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="t.ex. Lokalens skick"
            disabled={busy}
            className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm shadow-sm focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
          />
          <span className="text-[10px] text-stone-500">
            Namnet visas vid bilagans referens i avtalet och kan ändras senare.
          </span>
        </label>

        {err && (
          <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            {err}
          </div>
        )}

        {hasRecommendations && (
          <div className="flex flex-col gap-2">
            {recommendations.map((rec, i) => (
              <button
                key={`${rec.key}/${rec.version}`}
                type="button"
                onClick={() => fromTemplate(rec)}
                disabled={busy}
                className={
                  i === 0
                    ? 'flex items-start gap-2 rounded-lg border border-stone-900 bg-stone-900 px-3 py-2.5 text-left text-xs text-white hover:bg-stone-800 disabled:opacity-50'
                    : 'flex items-start gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-left text-xs text-stone-800 hover:border-stone-400 hover:bg-stone-50 disabled:opacity-50'
                }
              >
                <FilePlus className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="flex flex-col gap-0.5">
                  <span className="font-medium">
                    Skapa från mall: {rec.module.metadata.shortTitle ?? rec.module.metadata.title}
                  </span>
                  {rec.module.metadata.description && (
                    <span
                      className={
                        i === 0
                          ? 'line-clamp-2 text-[10px] leading-relaxed text-stone-300'
                          : 'line-clamp-2 text-[10px] leading-relaxed text-stone-500'
                      }
                    >
                      {rec.module.metadata.description.split('\n')[0]}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-stone-400">
          <span className="h-px flex-1 bg-stone-200" />
          {hasRecommendations ? 'eller' : 'Lägg till'}
          <span className="h-px flex-1 bg-stone-200" />
        </div>

        <label
          className={`flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 border-dashed border-stone-300 bg-white px-4 py-6 text-center text-xs text-stone-500 hover:border-stone-400 hover:bg-stone-50 ${busy ? 'pointer-events-none opacity-50' : ''}`}
        >
          <Upload className="h-5 w-5" />
          <span className="font-medium text-stone-700">
            {busy ? 'Arbetar…' : 'Ladda upp PDF'}
          </span>
          <span className="text-[10px] text-stone-400">Endast PDF, max 25 MB</span>
          <input
            type="file"
            accept={BILAGA_ACCEPTED_MIME}
            onChange={onPick}
            className="hidden"
            disabled={busy}
          />
        </label>
      </div>
    </div>
  );
}
